/**
 * ThemeProvider Component
 * Provides theme context to the entire app and applies theme on load.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ThemePreset, applyTheme, themePresets } from "@/components/ThemePicker";
import { useToast } from "@/hooks/use-toast";

interface ThemeContextType {
  theme: ThemePreset;
  updateTheme: (theme: ThemePreset) => Promise<void>;
  isLoading: boolean;
}

const defaultContext: ThemeContextType = {
  theme: "coral",
  updateTheme: async () => {},
  isLoading: true,
};

const ThemeContext = createContext<ThemeContextType>(defaultContext);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemePreset>("coral");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Load theme from database on mount
  useEffect(() => {
    let cancelled = false;

    const applyThemeForUserId = async (userId: string) => {
      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("theme_preset")
          .eq("id", userId)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        if (profile?.theme_preset && profile.theme_preset in themePresets) {
          const savedTheme = profile.theme_preset as ThemePreset;
          setTheme(savedTheme);
          applyTheme(savedTheme);
        }
      } catch (error) {
        console.error("Error loading theme:", error);
      }
    };

    const loadTheme = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          await applyThemeForUserId(user.id);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadTheme();

    // IMPORTANT: do not return a Promise from this callback (can block sign-in)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        void applyThemeForUserId(session.user.id);
      } else if (event === "SIGNED_OUT") {
        setTheme("coral");
        applyTheme("coral");
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Save theme to database
  const updateTheme = useCallback(async (newTheme: ThemePreset) => {
    setTheme(newTheme);
    applyTheme(newTheme);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("profiles")
        .update({ theme_preset: newTheme })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Theme Updated! 🎨",
        description: `Switched to ${themePresets[newTheme].name}`,
      });
    } catch (error) {
      console.error("Error saving theme:", error);
      toast({
        title: "Couldn't save theme",
        description: "Your theme change wasn't saved to your account.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const contextValue = useMemo(() => ({ theme, updateTheme, isLoading }), [theme, updateTheme, isLoading]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  return useContext(ThemeContext);
}
